/**
 * Incidents Dashboard - Enterprise Grade
 * Quality incident tracking and management
 */

import React, { useState, useEffect } from 'react';
import {
  AlertTriangle, Plus, Eye, Edit3, Search, Filter, Download,
  CheckCircle, Clock, TrendingDown, TrendingUp, User, Building,
  Calendar, FileText, AlertCircle, XCircle
} from 'lucide-react';
import { qualityIncidents } from '../../data/mockData';
import { useAppContext } from '../../context/AppContext';
import { useToast } from '../Common/ToastProvider';

const IncidentsDashboard = () => {
  const { state } = useAppContext();
  const { showToast } = useToast();
  const selectedFacility = state.selectedFacility;
  
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadData();
  }, [selectedFacility]);

  useEffect(() => {
    filterData();
  }, [incidents, filterStatus, filterSeverity, searchTerm]);

  const loadData = () => {
    const facilityIncidents = qualityIncidents.filter(i => 
      i.facility.toLowerCase().includes(selectedFacility) || selectedFacility === 'all'
    );
    setIncidents(facilityIncidents);
  };

  const filterData = () => {
    let filtered = [...incidents];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(i => i.status.toLowerCase() === filterStatus.toLowerCase());
    }
    
    if (filterSeverity !== 'all') {
      filtered = filtered.filter(i => i.severity.toLowerCase() === filterSeverity.toLowerCase());
    }
    
    if (searchTerm) {
      filtered = filtered.filter(i =>
        i.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.resident.toLowerCase().includes(searchTerm.toLowerCase()) ||
        i.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredIncidents(filtered);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'Major': return 'bg-red-100 text-red-800 border-red-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'Near Miss': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'Minor': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Resolved': return 'bg-green-100 text-green-800';
      case 'Under Investigation': return 'bg-yellow-100 text-yellow-800';
      case 'Pending': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = {
    total: incidents.length,
    thisMonth: incidents.filter(i => {
      const date = new Date(i.date);
      const now = new Date();
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
    }).length,
    resolved: incidents.filter(i => i.status === 'Resolved').length,
    underInvestigation: incidents.filter(i => i.status === 'Under Investigation').length,
    bySeverity: {
      major: incidents.filter(i => i.severity === 'Major').length,
      medium: incidents.filter(i => i.severity === 'Medium').length,
      minor: incidents.filter(i => i.severity === 'Minor').length,
      nearMiss: incidents.filter(i => i.severity === 'Near Miss').length
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
              <AlertTriangle className="w-8 h-8" />
              Quality Incidents
            </h1>
            <p className="text-orange-100 text-lg">Incident tracking and management</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowNewModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Plus className="w-4 h-4" />
              Report Incident
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-semibold flex items-center gap-2 transition-all"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-gray-800">{stats.total}</div>
              <div className="text-xs text-gray-500">Total Incidents</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">All Time</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-blue-600">{stats.thisMonth}</div>
              <div className="text-xs text-gray-500">This Month</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Current Period</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-green-600">{stats.resolved}</div>
              <div className="text-xs text-gray-500">Resolved</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Completed</h3>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-yellow-200">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-xl">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-600">{stats.underInvestigation}</div>
              <div className="text-xs text-gray-500">Investigating</div>
            </div>
          </div>
          <h3 className="font-bold text-gray-800">Active</h3>
        </div>
      </div>

      {/* Severity Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {Object.entries(stats.bySeverity).map(([severity, count]) => (
          <div key={severity} className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 capitalize">{severity.replace(/([A-Z])/g, ' $1').trim()}</h3>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(severity.charAt(0).toUpperCase() + severity.slice(1))}`}>
                {severity.charAt(0).toUpperCase() + severity.slice(1)}
              </span>
            </div>
            <div className="text-3xl font-bold text-gray-800">{count}</div>
            <div className="text-sm text-gray-600">incidents</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search incidents by type, resident, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="resolved">Resolved</option>
            <option value="under investigation">Under Investigation</option>
            <option value="pending">Pending</option>
          </select>
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="all">All Severity</option>
            <option value="major">Major</option>
            <option value="medium">Medium</option>
            <option value="minor">Minor</option>
            <option value="near miss">Near Miss</option>
          </select>
        </div>
      </div>

      {/* Incidents List */}
      <div className="space-y-4">
        {filteredIncidents.map((incident) => (
          <div key={incident.id} className="bg-white rounded-xl p-6 shadow-lg border-2 border-gray-200 hover:border-orange-300 transition-all">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-800">{incident.type}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(incident.severity)}`}>
                        {incident.severity}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(incident.status)}`}>
                        {incident.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <User className="w-4 h-4" />
                        {incident.resident}
                      </span>
                      <span className="flex items-center gap-1">
                        <Building className="w-4 h-4" />
                        {incident.facility}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {incident.date}
                      </span>
                    </div>
                    <p className="text-gray-700 mb-3">{incident.description}</p>
                    {incident.immediateAction && (
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mb-2">
                        <div className="text-xs font-semibold text-blue-800 mb-1">Immediate Action:</div>
                        <div className="text-sm text-blue-700">{incident.immediateAction}</div>
                      </div>
                    )}
                    {incident.followUp && (
                      <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                        <div className="text-xs font-semibold text-green-800 mb-1">Follow-up:</div>
                        <div className="text-sm text-green-700">{incident.followUp}</div>
                      </div>
                    )}
                    {incident.preventionMeasures && incident.preventionMeasures.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs font-semibold text-gray-700 mb-1">Prevention Measures:</div>
                        <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                          {incident.preventionMeasures.map((measure, index) => (
                            <li key={index}>{measure}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 ml-4">
                <button
                  onClick={() => setSelectedIncident(incident)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="View Details"
                >
                  <Eye className="w-5 h-5" />
                </button>
                <button
                  onClick={() => showToast('Edit incident feature coming soon', 'info')}
                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-all"
                  title="Edit"
                >
                  <Edit3 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Incident Detail Modal */}
      {selectedIncident && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl p-6 max-w-3xl w-full mx-4 my-8 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">
                {selectedIncident.type} - {selectedIncident.id}
              </h2>
              <button
                onClick={() => setSelectedIncident(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="text-sm text-gray-600 mb-1">Severity</div>
                  <div className="text-lg font-bold text-orange-600">{selectedIncident.severity}</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Status</div>
                  <div className="text-lg font-bold text-blue-600">{selectedIncident.status}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Resident</div>
                  <div className="text-lg font-bold text-green-600">{selectedIncident.resident}</div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="text-sm text-gray-600 mb-1">Date</div>
                  <div className="text-lg font-bold text-purple-600">{selectedIncident.date}</div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedIncident.description}</p>
              </div>

              {selectedIncident.immediateAction && (
                <div>
                  <h4 className="font-semibold mb-2">Immediate Action</h4>
                  <p className="text-gray-700 bg-blue-50 p-4 rounded-lg">{selectedIncident.immediateAction}</p>
                </div>
              )}

              {selectedIncident.followUp && (
                <div>
                  <h4 className="font-semibold mb-2">Follow-up</h4>
                  <p className="text-gray-700 bg-green-50 p-4 rounded-lg">{selectedIncident.followUp}</p>
                </div>
              )}

              {selectedIncident.preventionMeasures && selectedIncident.preventionMeasures.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Prevention Measures</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 bg-yellow-50 p-4 rounded-lg">
                    {selectedIncident.preventionMeasures.map((measure, index) => (
                      <li key={index}>{measure}</li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedIncident.staffInvolved && (
                <div>
                  <h4 className="font-semibold mb-2">Staff Involved</h4>
                  <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{selectedIncident.staffInvolved}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Incident Modal */}
      {showNewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-orange-800 flex items-center gap-2">
                <Plus className="w-6 h-6 text-orange-600" />
                Report Quality Incident
              </h2>
              <button
                onClick={() => setShowNewModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Incident Type</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500">
                    <option>Fall</option>
                    <option>Medication Error</option>
                    <option>Infection Control</option>
                    <option>Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500">
                    <option>Minor</option>
                    <option>Medium</option>
                    <option>Major</option>
                    <option>Near Miss</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 h-32"
                  placeholder="Provide detailed description of the incident..."
                ></textarea>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowNewModal(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    showToast('Incident reported successfully', 'success');
                    setShowNewModal(false);
                    loadData();
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold"
                >
                  Report Incident
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                <Download className="w-6 h-6 text-orange-600" />
                Export Incidents
              </h2>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={() => {
                  const csv = generateCSV();
                  downloadFile(csv, `incidents-${selectedFacility}.csv`, 'text/csv');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as CSV
              </button>
              <button
                onClick={() => {
                  const json = JSON.stringify(incidents, null, 2);
                  downloadFile(json, `incidents-${selectedFacility}.json`, 'application/json');
                  setShowExportModal(false);
                }}
                className="w-full px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-semibold flex items-center justify-center gap-2"
              >
                <FileText className="w-5 h-5" />
                Export as JSON
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  function generateCSV() {
    const headers = ['ID', 'Type', 'Resident', 'Facility', 'Date', 'Severity', 'Status'];
    const rows = incidents.map(i => [
      i.id,
      i.type,
      i.resident,
      i.facility,
      i.date,
      i.severity,
      i.status
    ].join(','));
    return [headers.join(','), ...rows].join('\n');
  }

  function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

export default IncidentsDashboard;
